import React, { useMemo } from 'react';
import { Button, makeStyles, Paper } from '@material-ui/core';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import StudentAssessmentAcceleratorTable from './components/StudentAssessmentAcceleratorTable';
import useStore from 'store';
import ViewAllAttemptsModal from '../../../AccessmentAcceleratorComponents/ViewAllAttemptsModal';
import AllModuleAssessmentAcceleratorLayout from '../../../AccessmentAcceleratorComponents/AllModuleAssessmentAcceleratorLayout';
import SingleModuleAttemptHeader from './components/SingleModuleAttemptHeader';
import ModuleTabSelector from '../../../AccessmentAcceleratorComponents/ModuleTabSelector';
import {
  buildAllModuleAssessmentData,
  buildStudentAssessmentHeaderList,
  buildSingleStudentModuleData,
  isAllSelected,
  getHasTwoAttempts,
  tableHasData,
  hasTableAttemptData
} from '../../../keyboarding-report-util';
import {
  KeyStudentEngagementAssessmentAcceleratorResult,
  Student
} from 'types/schema-generated-types';
import MaybeShowErrorMessageWithLoading from 'components/ReportsModule/components/common/MaybeShowErrorMessageWithLoading';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useTranslation } from 'react-i18next';
import useBooleanSwitch from '../../../hooks/use-boolean-switch';
import useModuleTabSelector from '../../../hooks/use-module-tab-selector';

type Props = {
  reportData?: KeyStudentEngagementAssessmentAcceleratorResult & { student: Student };
  loading?: boolean;
};

const StudentAssessmentAcceleratorTableCard: React.FC<Props> = ({ reportData, loading }) => {
  const classes = useStyles();
  const { t } = useTranslation(['ReportsHelpCards', 'ReportsHelpCommon']);
  const [, { openQuarterModal }] = useStore();
  const [{ selectedModule, showAll }, { setSelectedModule }] = useModuleTabSelector();
  const getData = useBooleanSwitch(
    buildAllModuleAssessmentData(reportData!),
    buildSingleStudentModuleData(reportData!, selectedModule)
  );

  const { data, selectedModuleData, attempt1Completed, attempt2Completed } = getData(showAll);

  const studentData: Student = (data as any)?.[0]?.student!;
  const handleViewAllAttempts = () => {
    openQuarterModal({
      body: (
        <ViewAllAttemptsModal
          studentData={studentData}
          module={selectedModule}
          moduleData={selectedModuleData!}
        />
      ),
      options: {
        paperProps: { className: classes.modalContainer },
        containerClass: classes.containerClass
      }
    });
  };

  const hasTwoAttempts = useMemo(
    () => getHasTwoAttempts(data),
    // eslint-disable-next-line
    [!isAllSelected(selectedModule)]
  );

  const maybeOutsideHeader = !isAllSelected(selectedModule) && {
    OutsideHeader: (
      <SingleModuleAttemptHeader
        title={selectedModule}
        showDivider={false}
        firstAttempt={attempt1Completed!}
        secondAttempt={attempt2Completed!}
        moduleData={selectedModuleData!}
        hasTwoAttempts={hasTwoAttempts}
      />
    )
  };

  const maybeViewAllAttempts = !isAllSelected(selectedModule) && {
    titleEndAdornment: (
      <Button
        disabled={data?.length === 0}
        size="small"
        variant="text"
        style={{ fontSize: '14px', fontWeight: 450 }}
        onClick={handleViewAllAttempts}
        startIcon={<ArrowForwardIcon />}
      >
        {t('viewAll', { ns: 'ReportsHelpCommon' })}
      </Button>
    )
  };
  const hasData = useMemo(() => tableHasData(data) || hasTableAttemptData(data), [data]);

  return (
    <Paper>
      <ReportCard
        helpText={t('assessmentAccelerator', { ns: 'ReportsHelpCards' })}
        title="Assessment Accelerator"
        {...maybeViewAllAttempts}
        loading={loading!}
        endAdornment={
          reportData?.activityTypes?.length! > 0 && (
            <ModuleTabSelector onChange={setSelectedModule} selectedModule={selectedModule} />
          )
        }
        reportDataProp="activityType"
        reportData={reportData?.activityTypes}
      >
        <MaybeShowErrorMessageWithLoading shouldRender={hasData} loading={loading!}>
          <AllModuleAssessmentAcceleratorLayout
            {...maybeOutsideHeader}
            showDivider={false}
            TopComponent={
              <StudentAssessmentAcceleratorTable selectedModule={selectedModule} data={data} />
            }
            headerList={buildStudentAssessmentHeaderList(
              reportData!,
              t,
              isAllSelected(selectedModule)
            )}
            title={selectedModule}
          />
        </MaybeShowErrorMessageWithLoading>
      </ReportCard>
    </Paper>
  );
};

const useStyles = makeStyles(() => ({
  modalContainer: {
    width: 400
  },
  containerClass: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  }
}));

export default StudentAssessmentAcceleratorTableCard;
